<template>
  <!--
    Diagrama del flujo de datos de Nukli, en el estilo de los mockups (marco de
    ventana + paneles). Tres piezas: el buzón del usuario, el servidor de Nukli
    en la UE (con lo que corre en infraestructura propia dentro) y los modelos
    de IA, con las garantías de cada salto en el propio dibujo.
    Todo el texto sale de i18n (security.diagram); los nombres propios, no.
  -->
  <div class="sd lp-frame" role="img" :aria-label="d.server + ' — ' + d.serverLoc">
    <div class="sd-bar">
      <span class="sd-dot"></span><span class="sd-dot"></span><span class="sd-dot"></span>
      <span class="sd-bar-title"><l-icon size="12">shield-check</l-icon>{{ d.barTitle }}</span>
    </div>

    <div class="sd-body">
      <!-- ============ Tu buzón ============ -->
      <div class="sd-node sd-mailbox">
        <div class="sd-node-head">
          <span class="sd-node-icon"><l-icon size="16" color="primary">mail</l-icon></span>
          <b>{{ d.mailbox }}</b>
        </div>
        <ul class="sd-items">
          <li><span class="sd-prov-dot"></span>Gmail</li>
          <li><span class="sd-prov-dot"></span>Outlook / M365</li>
          <li><span class="sd-prov-dot"></span>IMAP</li>
        </ul>
        <span class="sd-tag sd-tag--neutral"><l-icon size="11">key-round</l-icon>OAuth 2.0</span>
      </div>

      <!-- flecha buzón ⇄ servidor -->
      <div class="sd-link">
        <span class="sd-link-label"><l-icon size="11">lock</l-icon>TLS</span>
        <svg class="sd-arrow" viewBox="0 0 64 24" aria-hidden="true">
          <path d="M6 12h52" />
          <path d="m12 6-6 6 6 6" />
          <path d="m52 6 6 6-6 6" />
        </svg>
        <span class="sd-link-sub">{{ d.sync }}</span>
      </div>

      <!-- ============ Nukli Cloud (UE) ============ -->
      <div class="sd-node sd-server">
        <div class="sd-node-head">
          <span class="sd-node-icon sd-node-icon--accent"><l-icon size="16" color="white">server</l-icon></span>
          <div class="sd-server-title">
            <b>{{ d.server }}</b>
            <span class="sd-loc"><span class="sd-eu">EU</span>{{ d.serverLoc }}</span>
          </div>
        </div>
        <ul class="sd-checks">
          <li><l-icon size="14" color="primary">lock-keyhole</l-icon>{{ d.at1 }}</li>
          <li><l-icon size="14" color="primary">scan-search</l-icon>{{ d.at2 }}</li>
          <li><l-icon size="14" color="primary">scroll-text</l-icon>{{ d.at3 }}</li>
        </ul>
        <!-- lo que corre en hardware propio, DENTRO del perímetro -->
        <div class="sd-inhouse">
          <div class="sd-inhouse-head"><l-icon size="13">cpu</l-icon>{{ d.inhouse }}</div>
          <div class="sd-inhouse-chips">
            <span>{{ d.in1 }}</span><span>{{ d.in2 }}</span><span>{{ d.in3 }}</span>
          </div>
          <span class="sd-tag sd-tag--green"><l-icon size="11">shield-check</l-icon>{{ d.inNote }}</span>
        </div>
      </div>

      <!-- flecha servidor ⇄ IA -->
      <div class="sd-link">
        <span class="sd-link-label sd-link-label--amber"><l-icon size="11">hand</l-icon>{{ d.aiWhen }}</span>
        <svg class="sd-arrow" viewBox="0 0 64 24" aria-hidden="true">
          <path d="M6 12h52" />
          <path d="m12 6-6 6 6 6" />
          <path d="m52 6 6 6-6 6" />
        </svg>
        <span class="sd-link-sub">TLS</span>
      </div>

      <!-- ============ Modelos IA ============ -->
      <div class="sd-node sd-ai">
        <div class="sd-node-head">
          <span class="sd-node-icon"><l-icon size="16" color="primary">sparkles</l-icon></span>
          <b>{{ d.ai }}</b>
        </div>
        <ul class="sd-items">
          <li><span class="sd-prov-dot sd-prov-dot--g"></span>Google Gemini</li>
          <li><span class="sd-prov-dot sd-prov-dot--a"></span>Anthropic Claude</li>
        </ul>
        <span class="sd-tag sd-tag--green"><l-icon size="11">graduation-cap</l-icon>{{ d.aiTrain }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import LIcon from '@/components/LIcon.vue';

export default {
  name: 'SecurityDiagram',
  components: { LIcon },
  computed: {
    d() {
      return this.$t('security.diagram');
    },
  },
};
</script>

<style scoped>
.sd {
  background: var(--lp-surface);
  overflow: hidden;
  /* .lp-frame trae line-height:0 (pensado para capturas); aquí hay texto. */
  line-height: 1.4;
}

/* barra de ventana, como mk-bar */
.sd-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--lp-line);
  background: var(--lp-bg-subtle);
}
.sd-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--lp-line-strong);
}
.sd-bar-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: var(--lp-muted);
}

.sd-body {
  display: grid;
  grid-template-columns: 1fr auto 1.35fr auto 1fr;
  align-items: center;
  gap: 18px;
  padding: 34px 30px;
}

/* ---- nodos ---- */
.sd-node {
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  border-radius: 14px;
  padding: 18px;
  min-width: 0;
}
.sd-node-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.95rem;
  color: var(--lp-ink);
}
.sd-node-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lp-accent-weak);
}
.sd-node-icon--accent { background: var(--lp-accent); }

/* el servidor es el protagonista */
.sd-server {
  background: var(--lp-surface);
  border-color: var(--lp-line-strong);
  box-shadow: var(--lp-shadow-md);
  border-radius: 16px;
  padding: 20px;
}
.sd-server-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.sd-loc {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--lp-muted);
}
.sd-eu {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--lp-accent);
  background: var(--lp-accent-weak);
  border-radius: 5px;
  padding: 1px 5px;
}

/* listas simples (proveedores, modelos) */
.sd-items {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--lp-ink-2);
}
.sd-items li {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sd-prov-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--lp-accent);
  opacity: 0.55;
  flex-shrink: 0;
}
.sd-prov-dot--g { opacity: 0.85; }
.sd-prov-dot--a { opacity: 0.35; }

/* checklist del servidor */
.sd-checks {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--lp-ink-2);
}
.sd-checks li {
  display: flex;
  align-items: center;
  gap: 9px;
}

/* sub-panel de infraestructura propia */
.sd-inhouse {
  border: 1px dashed var(--lp-line-strong);
  border-radius: 11px;
  padding: 12px 14px;
  background: var(--lp-bg-subtle);
}
.sd-inhouse-head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--lp-ink);
  margin-bottom: 9px;
}
.sd-inhouse-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.sd-inhouse-chips span {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--lp-ink-2);
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
  border-radius: 999px;
  padding: 3px 9px;
}

/* etiquetas de garantía */
.sd-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
}
.sd-tag--neutral {
  color: var(--lp-ink-2);
  background: var(--lp-surface);
  border: 1px solid var(--lp-line);
}
.sd-tag--green {
  color: #15803d;
  background: rgba(22, 163, 74, 0.1);
}
.theme--dark .sd-tag--green {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.12);
}

/* ---- flechas ---- */
.sd-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 74px;
}
.sd-arrow {
  width: 58px;
  height: 22px;
  stroke: var(--lp-line-strong);
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.sd-link-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--lp-accent);
  background: var(--lp-accent-weak);
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;
}
.sd-link-label--amber {
  color: #b45309;
  background: rgba(245, 158, 11, 0.12);
}
.theme--dark .sd-link-label--amber {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.12);
}
.sd-link-sub {
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--lp-muted);
  white-space: nowrap;
}

/* ---- responsive: columna vertical ---- */
@media (max-width: 859px) {
  .sd-body {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 24px 18px;
  }
  .sd-link {
    flex-direction: row;
    justify-content: center;
    gap: 10px;
  }
  .sd-arrow {
    width: 22px;
    height: 40px;
    transform: rotate(90deg);
  }
}
</style>
